// Typed entry points for AuthSession over the legacy token storage.
// All writes/reads delegate to utils/auth.js — the single source of truth for
// how tokens are persisted (jwt-tokens[email.toLowerCase()] maps keyed by the
// current-user key). This wrapper adds TS types and session-awareness:
//   - readTokenRaw / readRefreshTokenRaw prefer sessionStorage imp-tokens when
//     this tab is impersonating; fall back to localStorage for normal sessions.
//   - saveTokenRaw / saveRefreshTokenRaw route writes to the correct store:
//     sessionStorage imp-tokens for impersonation tabs, localStorage otherwise.

import * as auth from '@/utils/auth';

import {
  clearImpFp,
  clearImpTokens,
  isImpersonating,
  readAccessToken,
  readImpTokens,
  readRefreshToken,
  saveImpTokens,
} from './tabStorage';

export const readUser = (): string | null => auth.readUser();

export const readTokenRaw = (): string | null => readAccessToken();

export const saveTokenRaw = (token: string, email: string): void => {
  if (isImpersonating()) {
    const existing = readImpTokens();
    saveImpTokens({ accessToken: token, refreshToken: existing?.refreshToken ?? '' });
  } else {
    auth.saveToken(token, email);
  }
};

export const readRefreshTokenRaw = (): string | null => readRefreshToken();

export const saveRefreshTokenRaw = (token: string, email: string): void => {
  if (isImpersonating()) {
    const existing = readImpTokens();
    saveImpTokens({ accessToken: existing?.accessToken ?? '', refreshToken: token });
  } else {
    auth.saveRefreshToken(token, email);
  }
};

// Atomic context-aware wipe. Under impersonation it clears only the per-tab imp session so the admin's
// localStorage tokens stay intact (they may still be valid), otherwise it deletes the admin tokens from localStorage
export const clearAuthTokens = (): void => {
  if (isImpersonating()) {
    clearImpTokens();
    clearImpFp();
    return;
  }
  auth.deleteToken();
  auth.deleteRefreshToken();
};
